// =============================================================================
// Ràng buộc & tiện ích thuần cho file chứng từ chi (không phụ thuộc DOM/network).
// NGUỒN SỰ THẬT về định dạng/size cho phép — expense-storage.ts import từ đây.
// =============================================================================

export const EXPENSE_DOC_MAX_BYTES = 15 * 1024 * 1024
export const EXPENSE_DOC_ALLOWED_MIME = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/avif',
  'application/pdf', 'text/xml', 'application/xml',
])
export const EXPENSE_DOC_ACCEPT = 'image/*,application/pdf,.xml,text/xml,application/xml'

// Hình dạng tối thiểu của File mà logic cần — để test không phải dựng File thật.
export type FileLike = { name: string; size: number; type: string }
export type DocRejectReason = 'type' | 'size' | 'duplicate'

// Gộp file mới chọn vào danh sách đã có: loại sai định dạng / quá size / trùng
// (tên + size — so với existing lẫn trong cùng batch). Trả danh sách mới + lý do loại.
export function mergeExpenseDocFiles<T extends FileLike>(
  existing: T[],
  incoming: T[],
): { next: T[]; rejected: { file: T; reason: DocRejectReason }[] } {
  const seen = new Set(existing.map((f) => `${f.name} ${f.size}`))
  const next = [...existing]
  const rejected: { file: T; reason: DocRejectReason }[] = []
  for (const file of incoming) {
    if (!EXPENSE_DOC_ALLOWED_MIME.has(file.type)) {
      rejected.push({ file, reason: 'type' })
      continue
    }
    if (file.size > EXPENSE_DOC_MAX_BYTES) {
      rejected.push({ file, reason: 'size' })
      continue
    }
    const key = `${file.name} ${file.size}`
    if (seen.has(key)) {
      rejected.push({ file, reason: 'duplicate' })
      continue
    }
    seen.add(key)
    next.push(file)
  }
  return { next, rejected }
}

// Dung lượng dễ đọc: <1 KB → B, <1 MB → KB (số nguyên), còn lại → MB (1 số lẻ, bỏ .0).
export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  const kb = Math.round(n / 1024)
  if (kb < 1024) return `${kb} KB`
  const mb = n / (1024 * 1024)
  const s = mb.toFixed(1).replace(/\.0$/, '')
  return `${s} MB`
}

// ---------------------------------------------------------------------------
// Cấu trúc thư mục lưu Drive: <YYYY-MM>/<loại phiếu>/<DD>/<tên nhân viên>
// (4 cấp — khớp giới hạn sanitizeSubPath của backend /expense-docs).
// ---------------------------------------------------------------------------

// Khoá tháng/ngày từ timestamp TẠO phiếu. Thiếu/lỗi → thời điểm hiện tại (giữ semantics
// expenseMonthKey cũ). Dùng local time.
export function expenseDateKeys(iso: string | null | undefined): { monthKey: string; dayKey: string } {
  const d = iso ? new Date(iso) : new Date()
  const src = Number.isNaN(d.getTime()) ? new Date() : d
  return {
    monthKey: `${src.getFullYear()}-${String(src.getMonth() + 1).padStart(2, '0')}`,
    dayKey: String(src.getDate()).padStart(2, '0'),
  }
}

// Tên nhân viên cho thư mục: GIỮ tiếng Việt (dễ đọc), chỉ bỏ ký tự đường dẫn/cấm.
export function folderSeg(s: string): string {
  const t = s.trim().replace(/[/\\<>:"|?*]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100)
  return t || 'khong-ro'
}

// Đường dẫn thư mục con trên Drive. Nhánh 'DNTT' (phiếu chi), 'Dat coc' (đặt cọc)
// hoặc 'Ha COD' (chứng từ đề nghị / xác nhận hạ COD).
export function buildExpenseDocSubfolder(
  createdAt: string | null | undefined,
  employee: string,
  branch: 'DNTT' | 'Dat coc' | 'Ha COD' = 'DNTT',
): string {
  const { monthKey, dayKey } = expenseDateKeys(createdAt)
  return `${monthKey}/${branch}/${dayKey}/${folderSeg(employee)}`
}

// --- Đánh số mã chứng từ (<base>-<n>) khi upload NHIỀU file cùng lúc ------------
// Số kế tiếp = max suffix `-<n>` của các mã đã có + 1 (không có → 1).
export function nextDocSeq(existingCodes: string[]): number {
  const max = existingCodes.reduce((m, c) => {
    const s = c.match(/-(\d+)$/)
    return Math.max(m, s ? Number(s[1]) : 0)
  }, 0)
  return max + 1
}

// Mã cho `count` file liên tiếp bắt đầu từ `start`.
export function buildSeqCodes(base: string, start: number, count: number): string[] {
  return Array.from({ length: count }, (_, i) => `${base}-${start + i}`)
}

// --- Dựng tên file có cấu trúc: <code>_<label>_<slug tên gốc>.<ext> ---------
// Bỏ dấu tiếng Việt → ascii cho phần tên gốc (giữ code/label nguyên).
function deaccent(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').replace(/đ/g, 'd').replace(/Đ/g, 'D')
}
function slug(s: string): string {
  return deaccent(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}
function splitExt(name: string): { base: string; ext: string } {
  const i = name.lastIndexOf('.')
  if (i <= 0) return { base: name, ext: '' }
  return { base: name.slice(0, i), ext: name.slice(i + 1).toLowerCase() }
}
export function buildDocFileName(code: string, label: string, originalName: string): string {
  const { base, ext } = splitExt(originalName)
  const b = slug(base) || 'file'
  return `${code}_${label}_${b}${ext ? '.' + ext : ''}`
}
